<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import TvImage from '@/assets/images/movie.png'
import SpinImage from '@/assets/images/fortune-wheel.webp'
import { useAd } from '@/composables/game/ad'
import { computed, ref, watch } from 'vue'
import { DateTime } from 'luxon'

const props = defineProps<{
  show: boolean
  isLoading: boolean
}>()
const emit = defineEmits<{
  (event: 'hide'): void,
  (event: 'claim', withDelay: boolean): void
}>()

const adWatched = ref<boolean>(false)
const hasClaimed = ref<boolean>(false)
const {
  claimed,
  showAd,
  maxDailyAdsCount,
  watchedDailyAdsCount,
  adWatchedSpinReward,
  countdownTimeBetweenAds,
  showAdsCountdown,
} = useAd()
const disabledButton = ref<boolean>(false)
const shouldShowCountDown = computed(() => showAdsCountdown.value && hasClaimed.value)

const loadingButton = ref<boolean>(props.isLoading)

async function check() {
  if (claimed.value && !adWatched.value) {
    onHide()
    return
  }
  if (!adWatched.value) {
    disabledButton.value = true
    setTimeout(() => {
      disabledButton.value = false
    }
    , 1000,
    )
    hasClaimed.value = false
    loadingButton.value = true
    adWatched.value = await showAd()
    loadingButton.value = false
  } else {
    emit('claim', true)
    adWatched.value = false
    hasClaimed.value = true
  }
}

function onHide() {
  emit('hide')
  if (adWatched.value) {
    emit('claim', false)
  }
  adWatched.value = false
}

watch(() => props.show, () => {
  if (props.show) {
    adWatched.value = false
  }
})

watch(() => props.isLoading, () => {
  loadingButton.value = props.isLoading
})

</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="onHide"
  >
    <div class="flex flex-col gap-y-8">
      <div
        class="w-full flex flex-col items-center gap-y-6"
      >
        <img
          class="w-[120px] h-[120px] min-w-[120px] object-cover"
          :src="TvImage"
        >
        <div class="flex flex-col items-center">
          <div
            class="text-center text-2xl font-semibold -tracking-[0.48px] text-light-white"
          >
            <T
              key-name="task.ads.title"
              default-value="Watch Ads"
            />
          </div>
          <div
            class="text-center text-sm font-medium text-white/60"
          >
            <T
              key-name="task.ads.description"
              default-value="You can watch up to {maxDaily} Ads per day."
              :params="{maxDaily: maxDailyAdsCount}"
            />
          </div>
        </div>
        <div class="flex items-center gap-x-2">
          <img
            :src="SpinImage"
            class="size-8"
          >
          <span
            class="text-xl leading-6 font-bold -tracking-[0.4px] text-teal-primary"
          >
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: adWatchedSpinReward}"
            />
          </span>
        </div>
        <p class="text-sm text-white/[87%] font-medium">
          <T
            key-name="fortune_wheel.info.more_spins.count_watched"
            default-value="{watchedCount}/{totalCount} watched"
            :params="{
              watchedCount: watchedDailyAdsCount,
              totalCount: maxDailyAdsCount
            }"
          />
        </p>
      </div>
      <BaseButton
        style-type="primary-pink"
        class="w-full"
        :disabled="disabledButton || shouldShowCountDown || (claimed && !adWatched)"
        :loading="loadingButton"
        @click="check"
      >
        <template v-if="shouldShowCountDown && !isLoading">
          <T
            key-name="tasks.ads.wait"
            default-value="Wait"
          />
          <span>{{ " " }}</span>
          <span>{{ DateTime.fromSeconds(countdownTimeBetweenAds).toFormat('mm:ss') }}</span>
        </template>
        <T
          v-else-if="!adWatched && !hasClaimed && loadingButton"
          key-name="quests.claim"
          default-value="Watching"
        />
        <T
          v-else-if="adWatched || loadingButton"
          key-name="quests.claim"
          default-value="Claim"
        />
        <T
          v-else-if="claimed"
          key-name="tasks.ads.come_back"
          default-value="Come back tomorrow"
        />
        <T
          v-else
          key-name="task.ads.cta"
          default-value="Watch Ad"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>
